import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { QueueListIcon, AdjustmentsHorizontalIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  Button, Container, Empty, Eyebrow, Page, PageHeader, PlatformLogo, PostCard,
  Sheet, SheetBody, SheetFooter, SheetHeader, Subtitle, Title,
} from '@monorepo/common/components/ui';
import {
  PLATFORM_KEYS, platformLabel,
} from '@monorepo/common/utils/platforms';
import { PostsActions } from '../components/PostsTabs';

const API_BASE_URL = import.meta.env?.VITE_API_BASE_URL || '/api';
const PAGE_SIZE = 40;

const SORTS = [
  ['published', 'Newest'],
  ['score', 'Score'],
  ['lift', 'Lift'],
  ['views', 'Views'],
];

/**
 * Every post across every connected account.
 *
 * Filter state lives in the URL, not in component state, so a filtered view is
 * a link — the dashboard's "View all" entries point straight at
 * ?tier=breaking_out and ?sort=score, and a useful filter survives a refresh
 * and can be sent to someone.
 */
/**
 * The filter controls themselves, rendered inline on desktop and inside a
 * sheet on a phone. One definition, so the two can never drift apart.
 */
const FilterControls = ({
  stacked, platforms, accountKeys, visibleAccounts, sort, tier,
  onTogglePlatform, onToggleAccount, onUpdate,
}) => {
  const rowClass = stacked
    ? 'flex flex-wrap gap-2'
    : '-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden';

  return (
    <>
      <div className={stacked ? 'mb-5' : ''}>
        {stacked && <p className="mb-2 text-caption uppercase tracking-[0.1em] text-ink-subtle">Platform</p>}
        <div className={rowClass}>
          {PLATFORM_KEYS.map((key) => {
            const on = platforms.includes(key);
            return (
              <button
                key={key}
                type="button"
                onClick={() => onTogglePlatform(key)}
                aria-pressed={on}
                className={[
                  'inline-flex shrink-0 items-center gap-2 rounded-pill border px-3',
                  'sm:min-h-[34px] text-caption font-medium transition-colors',
                  on
                    ? 'border-transparent bg-accent text-accent-ink'
                    : 'border-line bg-surface text-ink-muted hover:border-line-strong hover:text-ink',
                ].join(' ')}
              >
                <PlatformLogo platform={key} label={null} className="size-3.5" />
                {platformLabel(key)}
              </button>
            );
          })}
        </div>
      </div>

      {visibleAccounts.length > 1 && (
        <div className={stacked ? 'mb-5' : ''}>
          {stacked && <p className="mb-2 text-caption uppercase tracking-[0.1em] text-ink-subtle">Account</p>}
          <div className={rowClass}>
            {visibleAccounts.map((a) => {
              // Qualified by platform: two accounts on different platforms can
              // carry the same encoded id, which made this key non-unique AND
              // made selecting one select the other.
              const key = `${a.platform}:${a.account_id}`;
              const on = accountKeys.includes(key);
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => onToggleAccount(key)}
                  aria-pressed={on}
                  className={[
                    'inline-flex shrink-0 items-center gap-1.5 rounded-pill border px-3',
                    'sm:min-h-[30px] text-caption transition-colors',
                    on
                      ? 'border-ink bg-surface-raised text-ink'
                      : 'border-line bg-surface text-ink-subtle hover:text-ink',
                  ].join(' ')}
                >
                  <PlatformLogo platform={a.platform} label={platformLabel(a.platform)}
                                className="size-3 shrink-0" />
                  @{a.username || a.account_id}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className={stacked ? 'space-y-5' : 'flex flex-wrap items-center gap-2'}>
        <div>
          {stacked && <p className="mb-2 text-caption uppercase tracking-[0.1em] text-ink-subtle">Sort</p>}
          <div className="inline-flex items-center gap-1 rounded-pill border border-line bg-surface p-1">
            {SORTS.map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => onUpdate('sort', key === 'published' ? '' : key)}
                aria-pressed={sort === key}
                className={[
                  'rounded-pill px-3 text-caption font-medium transition-colors sm:min-h-[28px]',
                  sort === key ? 'bg-accent text-accent-ink' : 'text-ink-muted hover:text-ink',
                ].join(' ')}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <button
          type="button"
          onClick={() => onUpdate('tier', tier === 'breaking_out' ? '' : 'breaking_out')}
          aria-pressed={tier === 'breaking_out'}
          className={[
            'inline-flex items-center gap-1.5 rounded-pill border px-3',
            'sm:min-h-[30px] text-caption font-medium transition-colors',
            tier === 'breaking_out'
              ? 'border-success bg-success-tint text-success'
              : 'border-line bg-surface text-ink-muted hover:text-ink',
          ].join(' ')}
        >
          <span aria-hidden="true">▴</span> Breaking out only
        </button>
      </div>
    </>
  );
};

export default function PostTimeline() {
  const [params, setParams] = useSearchParams();

  const platforms = useMemo(
    () => (params.get('platforms') || '').split(',').filter(Boolean),
    [params]
  );
  /**
   * The selected accounts, as `platform:id` tokens.
   *
   * NOT bare ids. `account_id` is `encode_id(row.id)` and every platform has
   * its own table starting at 1, so the first TikTok account and the first
   * Instagram account carry the IDENTICAL encoded id — selecting one selected
   * both, here and in the request. The server accepts the bare form too, so an
   * older link still resolves.
   */
  const accountKeys = useMemo(
    () => (params.get('accounts') || '').split(',').filter(Boolean),
    [params]
  );
  const sort = params.get('sort') || 'published';
  const tier = params.get('tier') || '';
  const days = params.get('days') || '90';

  const [data, setData] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);

  // What the summary chip counts. Sort is excluded on purpose — it is always
  // set to something, so counting it would mean the chip never reads zero.
  const activeCount = platforms.length + accountKeys.length + (tier ? 1 : 0);

  // The account filter list comes from the summary endpoint, which already
  // enumerates every accessible account across platforms.
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE_URL}/social_stats/summary?days=7`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d) => !cancelled && setAccounts(d.accounts || []))
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Reset paging whenever the query changes, or "load more" would append the
  // next page of the PREVIOUS filter to the new one.
  useEffect(() => { setOffset(0); }, [params]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const qs = new URLSearchParams({ days, sort, limit: String(PAGE_SIZE), offset: String(offset) });
    if (platforms.length) qs.set('platforms', platforms.join(','));
    if (accountKeys.length) qs.set('accounts', accountKeys.join(','));
    if (tier) qs.set('tier', tier);

    fetch(`${API_BASE_URL}/social_stats/posts?${qs}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d) => {
        if (cancelled) return;
        setData((prev) =>
          offset > 0 && prev ? { ...d, posts: [...prev.posts, ...d.posts] } : d
        );
      })
      .catch((e) => !cancelled && setError(e.message))
      .finally(() => !cancelled && setLoading(false));

    return () => { cancelled = true; };
  }, [days, sort, tier, offset, platforms, accountKeys]);

  const update = useCallback(
    (key, value) => {
      const next = new URLSearchParams(params);
      if (value) next.set(key, value);
      else next.delete(key);
      setParams(next, { replace: true });
    },
    [params, setParams]
  );

  const togglePlatform = (key) => {
    const next = platforms.includes(key)
      ? platforms.filter((p) => p !== key)
      : [...platforms, key];
    update('platforms', next.join(','));
    // An account filter can name accounts on a platform just switched off.
    // Drop any that no longer apply rather than silently returning nothing.
    if (next.length && accountKeys.length) {
      const stillValid = accountKeys.filter((k) => next.includes(k.split(':')[0]));
      update('accounts', stillValid.join(','));
    }
  };

  const toggleAccount = (key) => {
    const next = accountKeys.includes(key)
      ? accountKeys.filter((a) => a !== key)
      : [...accountKeys, key];
    update('accounts', next.join(','));
  };

  const visibleAccounts = platforms.length
    ? accounts.filter((a) => platforms.includes(a.platform))
    : accounts;

  const posts = data?.posts || [];
  const hasMore = data ? posts.length < data.total : false;
  const filtered = platforms.length || accountKeys.length || tier;

  return (
    <Page>
      <Container>
        <PageHeader>
          <div className="min-w-0">
            <Eyebrow>
              <QueueListIcon aria-hidden="true" />
              Timeline
            </Eyebrow>
            <Title>Posts</Title>
            <Subtitle>
              Every post across every connected account, newest first.
            </Subtitle>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {filtered && (
              <Button
                variant="ghost"
                className="hidden sm:inline-flex"
                onClick={() => setParams(new URLSearchParams(), { replace: true })}
              >
                Clear filters
              </Button>
            )}
            <PostsActions current="published" />
          </div>
        </PageHeader>

        {/* ── Filters ─────────────────────────────────────────────────────
            Two presentations of one control set.

            On a phone this is a single sticky row that summarises the active
            filters and opens a sheet — the previous three stacked rows spent
            ~200px before the first post, on a screen whose whole job is
            showing posts. Sticky so it stays reachable while scrolling: it
            costs its height once rather than on every scroll back to the top.

            From `sm` up there is room, so the same controls render inline. */}
        <div className="sticky top-0 z-20 -mx-[max(1rem,var(--safe-left))] mb-2 bg-canvas/95 px-[max(1rem,var(--safe-left))] py-1 backdrop-blur sm:static sm:z-auto sm:mx-0 sm:mb-5 sm:bg-transparent sm:px-0 sm:py-0 sm:backdrop-blur-none">
          {/* Phone: summary row */}
          <div className="flex items-center gap-2 sm:hidden">
            <button
              type="button"
              onClick={() => setFiltersOpen(true)}
              className={[
                'inline-flex shrink-0 items-center gap-1.5 rounded-pill border px-3',
                'text-caption font-medium transition-colors',
                activeCount
                  ? 'border-transparent bg-accent text-accent-ink'
                  : 'border-line bg-surface text-ink-muted',
              ].join(' ')}
            >
              <AdjustmentsHorizontalIcon className="size-4" aria-hidden="true" />
              Filters
              {activeCount > 0 && <span className="tabular-nums">· {activeCount}</span>}
            </button>

            {/* Sort stays out of the sheet: it is changed far more often than
                the filters and is one tap this way. */}
            <div className="flex min-w-0 flex-1 gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {SORTS.map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => update('sort', key === 'published' ? '' : key)}
                  aria-pressed={sort === key}
                  className={[
                    'shrink-0 rounded-pill px-2.5 text-caption font-medium transition-colors',
                    sort === key ? 'bg-surface-raised text-ink' : 'text-ink-subtle',
                  ].join(' ')}
                >
                  {label}
                </button>
              ))}
            </div>

            {data && (
              <span className="shrink-0 text-caption-sm tabular-nums text-ink-subtle">
                {data.total}
              </span>
            )}
          </div>

          {/* sm and up: the controls inline */}
          <div className="hidden sm:block sm:space-y-2">
            <FilterControls
              platforms={platforms}
              accountKeys={accountKeys}
              visibleAccounts={visibleAccounts}
              sort={sort}
              tier={tier}
              onTogglePlatform={togglePlatform}
              onToggleAccount={toggleAccount}
              onUpdate={update}
            />
          </div>
        </div>

        {/* Phone filter sheet */}
        <Sheet
          open={filtersOpen}
          onClose={() => setFiltersOpen(false)}
          label="Filter posts"
          className="sm:hidden"
        >
          <SheetHeader>
            <span className="text-body font-medium text-ink">Filters</span>
            <Button variant="ghost" icon onClick={() => setFiltersOpen(false)} aria-label="Close" className="-mr-2">
              <XMarkIcon className="size-6" aria-hidden="true" />
            </Button>
          </SheetHeader>
          <SheetBody>
            <FilterControls
              stacked
              platforms={platforms}
              accountKeys={accountKeys}
              visibleAccounts={visibleAccounts}
              sort={sort}
              tier={tier}
              onTogglePlatform={togglePlatform}
              onToggleAccount={toggleAccount}
              onUpdate={update}
            />
          </SheetBody>
          <SheetFooter>
            {activeCount > 0 && (
              <Button variant="ghost" onClick={() => setParams(new URLSearchParams(), { replace: true })}>
                Clear all
              </Button>
            )}
            <Button variant="primary" onClick={() => setFiltersOpen(false)}>
              {/* Interpolating a missing count left "Show  posts" with a gap. */}
              {data ? `Show ${data.total} ${data.total === 1 ? 'post' : 'posts'}` : 'Show posts'}
            </Button>
          </SheetFooter>
        </Sheet>

        {error && (
          <p className="mb-4 rounded-field border border-danger bg-danger-tint p-3 text-body-sm text-danger">
            Could not load posts ({error}).
          </p>
        )}

        {!loading && posts.length === 0 && !error ? (
          <Empty
            title={filtered ? 'Nothing matches those filters' : 'No posts yet'}
            icon={<QueueListIcon className="h-8 w-8 text-ink-subtle" aria-hidden="true" />}
          >
            <p className="max-w-sm text-body-sm">
              {filtered
                ? 'Try widening the platform or account filter.'
                : 'Posts appear here once the daily stats snapshot has run for a connected account.'}
            </p>
          </Empty>
        ) : (
          <>
            <div className="space-y-2">
              {posts.map((post) => (
                /* The whole card is the link. A separate "Open" affordance
                   cost a line in the right-hand column and duplicated what
                   tapping the card already implies. */
                <PostCard key={`${post.platform}-${post.post_id}`} post={post} />
              ))}
            </div>

            {loading && (
              <p className="mt-4 text-center text-body-sm text-ink-subtle">Loading…</p>
            )}

            {hasMore && !loading && (
              <div className="mt-5 flex justify-center">
                <Button onClick={() => setOffset(posts.length)}>
                  Load more ({data.total - posts.length} left)
                </Button>
              </div>
            )}

            {data && posts.length > 0 && (
              <p className="mt-4 text-center text-caption text-ink-subtle">
                Showing {posts.length} of {data.total}
              </p>
            )}
          </>
        )}
      </Container>
    </Page>
  );
}
